import axios from "axios";
import {
  CONNECTION_ERROR_CODES,
  CONNECTION_ERROR_FALLBACK_MESSAGE,
  getConnectionErrorDefinition,
  RETRYABLE_CONNECTION_ERROR_CODES,
} from "../constants/connectionErrorCodes";
import type { ApiError, ConnectionHubError } from "../api/connectionHub.types";
import { connectionHubLogger } from "./logger";

const isApiError = (value: unknown): value is ApiError => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ApiError>;
  return candidate.success === false && typeof candidate.errorCode === "string";
};

export const isConnectionHubError = (value: unknown): value is ConnectionHubError => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ConnectionHubError>;
  return typeof candidate.code === "string" &&
    typeof candidate.message === "string" &&
    typeof candidate.retryable === "boolean";
};

const isRetryableStatus = (status?: number): boolean =>
  status === 429 || (typeof status === "number" && status >= 500);

/** Hub failures never toast: they render as an inline ErrorPanel where they happened. */
export interface HandleConnectionErrorOptions {
  report?: boolean;
}

export const handleConnectionError = (
  error: unknown,
  options: HandleConnectionErrorOptions = {}
): ConnectionHubError => {
  if (isConnectionHubError(error)) return error;

  let normalized: ConnectionHubError;
  if (axios.isAxiosError(error)) {
    const body = error.response?.data;
    if (isApiError(body)) {
      const statusCode = body.statusCode ?? error.response?.status;
      const definition = getConnectionErrorDefinition(body.errorCode);
      normalized = {
        code: body.errorCode,
        message: definition?.message || body.message || CONNECTION_ERROR_FALLBACK_MESSAGE,
        statusCode,
        details: body.details,
        retryable: definition?.retryable ||
          RETRYABLE_CONNECTION_ERROR_CODES.has(body.errorCode) ||
          isRetryableStatus(statusCode),
      };
    } else {
      const statusCode = error.response?.status;
      const code = error.response
        ? CONNECTION_ERROR_CODES.UNKNOWN_ERROR
        : CONNECTION_ERROR_CODES.NETWORK_ERROR;
      const responseMessage = body && typeof body === "object" &&
        typeof (body as { message?: unknown }).message === "string"
        ? (body as { message: string }).message
        : undefined;
      normalized = {
        code,
        message: responseMessage || getConnectionErrorDefinition(code)?.message || CONNECTION_ERROR_FALLBACK_MESSAGE,
        statusCode,
        retryable: !error.response || isRetryableStatus(statusCode),
      };
    }
  } else if (isApiError(error)) {
    const definition = getConnectionErrorDefinition(error.errorCode);
    normalized = {
      code: error.errorCode,
      message: definition?.message || error.message || CONNECTION_ERROR_FALLBACK_MESSAGE,
      statusCode: error.statusCode,
      details: error.details,
      retryable: definition?.retryable || isRetryableStatus(error.statusCode),
    };
  } else if (error instanceof Error) {
    normalized = {
      code: CONNECTION_ERROR_CODES.UNKNOWN_ERROR,
      message: error.message || CONNECTION_ERROR_FALLBACK_MESSAGE,
      retryable: false,
    };
  } else {
    normalized = {
      code: CONNECTION_ERROR_CODES.UNKNOWN_ERROR,
      message: CONNECTION_ERROR_FALLBACK_MESSAGE,
      retryable: false,
    };
  }

  if (options.report !== false) {
    connectionHubLogger.error(error, {
      errorCode: normalized.code,
      statusCode: normalized.statusCode,
      retryable: normalized.retryable,
    });
  }
  return normalized;
};
